import { useEffect, useRef } from "react";

const PASTEL_COLORS = [
  "#FFB3BA", "#FFDFBA", "#FFFFBA", "#BAFFC9", "#BAE1FF",
  "#F4B8E4", "#C9C4FF", "#FFC0D3", "#B8F0E6", "#FFD9B3",
  "#E8BAFF", "#BAFFD6", "#FFBAB3", "#B3FFEC", "#FFB3FF",
];

// Every MUI surface selector we want to flash
const SURFACE_SELECTORS = [
  ".MuiPaper-root",
  ".MuiCard-root",
  ".MuiAppBar-root",
  ".MuiToolbar-root",
  ".MuiDrawer-paper",
  // Calendar day cells
  ".MuiGrid-root > .MuiPaper-root",
  // Quick Links section cards
  ".MuiCardContent-root",
];

// Inline disco ball SVG as a data URI
const DISCO_CURSOR = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='32' height='32' viewBox='0 0 32 32'%3E%3Ccircle cx='16' cy='16' r='14' fill='%23cccccc' stroke='%23999' stroke-width='1'/%3E%3Crect x='7' y='7' width='4' height='4' rx='1' fill='%23ff8888' opacity='0.8'/%3E%3Crect x='13' y='5' width='4' height='4' rx='1' fill='%2388ff88' opacity='0.8'/%3E%3Crect x='19' y='7' width='4' height='4' rx='1' fill='%238888ff' opacity='0.8'/%3E%3Crect x='5' y='13' width='4' height='4' rx='1' fill='%23ffff88' opacity='0.8'/%3E%3Crect x='13' y='13' width='4' height='4' rx='1' fill='%23ffffff' opacity='0.9'/%3E%3Crect x='21' y='13' width='4' height='4' rx='1' fill='%23ff88ff' opacity='0.8'/%3E%3Crect x='7' y='19' width='4' height='4' rx='1' fill='%2388ffff' opacity='0.8'/%3E%3Crect x='13' y='21' width='4' height='4' rx='1' fill='%23ffaa88' opacity='0.8'/%3E%3Crect x='19' y='19' width='4' height='4' rx='1' fill='%23aaffaa' opacity='0.8'/%3E%3C/svg%3E") 16 16, auto`;

const BOUNCE_KEYFRAMES = `
@keyframes __disco_bounce {
  0%, 100% { transform: translateY(0) rotate(0deg); }
  25%       { transform: translateY(-10px) rotate(-4deg); }
  75%       { transform: translateY(-5px)  rotate(4deg); }
}
`;

interface DiscoModeProps {
  containerRef: React.RefObject<HTMLElement | null>;
  onEnd: () => void;
}

export default function DiscoMode({ containerRef, onEnd }: DiscoModeProps) {
  const intervalRef = useRef<ReturnType<typeof setInterval>>();
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  const styleTagRef = useRef<HTMLStyleElement | null>(null);
  const colorIdxRef = useRef(0);

  useEffect(() => {
    // Inject bounce keyframes into <head>
    const style = document.createElement("style");
    style.textContent = BOUNCE_KEYFRAMES;
    document.head.appendChild(style);
    styleTagRef.current = style;

    // ── Disco cursor on the whole page ──
    const origBodyCursor = document.body.style.cursor;
    document.body.style.cursor = DISCO_CURSOR;

    // ── Bounce headings: native + MUI Typography h* variants ──
    // The header title and the home page welcome heading are both captured here
    const headingSelectors = [
      "h1, h2, h3, h4, h5, h6",
      // MUI Typography h* variants render with these classes
      ".MuiTypography-h1, .MuiTypography-h2, .MuiTypography-h3",
      ".MuiTypography-h4, .MuiTypography-h5, .MuiTypography-h6",
      // Quick Links group labels (subtitle2)
      ".MuiTypography-subtitle2",
      // Header AppBar title
      ".MuiToolbar-root .MuiTypography-root",
    ].join(", ");

    const headings = Array.from(
      document.querySelectorAll<HTMLElement>(headingSelectors),
    ).filter((el) => {
      const r = el.getBoundingClientRect();
      return r.width > 0 && r.height > 0;
    });

    const origAnimations: string[] = [];
    headings.forEach((h, i) => {
      origAnimations[i] = h.style.animation;
      const delay = ((i * 0.12) % 0.5).toFixed(2);
      h.style.animation = `__disco_bounce 0.55s ease-in-out ${delay}s infinite`;
    });

    // ── Flash background: body + container + ALL MUI surfaces ──
    // MUI uses both `background` and `backgroundColor` — we need to handle both.
    // We also inject a CSS class so MUI's own inline styles don't win.
    const flashStyle = document.createElement("style");
    flashStyle.id = "__disco_flash";
    document.head.appendChild(flashStyle);

    const targets: HTMLElement[] = [
      document.body,
      ...(containerRef.current ? [containerRef.current] : []),
      // Every MUI surface (cards, papers, appbar, toolbar, drawer, etc.)
      ...Array.from(
        document.querySelectorAll<HTMLElement>(
          ".MuiPaper-root, .MuiCard-root, .MuiAppBar-root, " +
          ".MuiDrawer-root, .MuiToolbar-root, " +
          ".MuiGrid-root, .MuiBox-root",
        ),
      ),
    ];

    // Snapshot original inline styles
    const origBg = targets.map((t) => ({
      bg: t.style.background,
      bgColor: t.style.backgroundColor,
    }));

    intervalRef.current = setInterval(() => {
      colorIdxRef.current = (colorIdxRef.current + 1) % PASTEL_COLORS.length;

      // Build a CSS rule that overrides ALL MUI surface backgrounds using !important
      const rules = [
        `body { background-color: ${PASTEL_COLORS[colorIdxRef.current]} !important; }`,
        ...SURFACE_SELECTORS.map((sel, i) => {
          const hue = (colorIdxRef.current + i * 2) % PASTEL_COLORS.length;
          return `${sel} { background: ${PASTEL_COLORS[hue]} !important; background-color: ${PASTEL_COLORS[hue]} !important; }`;
        }),
      ].join("\n");

      flashStyle.textContent = rules;

      // Directly override inline `background` on [data-disco] elements
      // (dock / drawer use theme functions that produce inline styles which
      //  CSS class selectors cannot override — direct JS assignment wins)
      document
        .querySelectorAll<HTMLElement>("[data-disco]")
        .forEach((el, i) => {
          const hue = (colorIdxRef.current + i * 5) % PASTEL_COLORS.length;
          el.style.setProperty("background", PASTEL_COLORS[hue], "important");
        });
    }, 280);

    timerRef.current = setTimeout(() => {
      cleanup();
      onEnd();
    }, 10000);

    function cleanup() {
      clearInterval(intervalRef.current);
      clearTimeout(timerRef.current);
      document.body.style.cursor = origBodyCursor;
      styleTagRef.current?.remove();
      flashStyle.remove();

      // Restore headings
      headings.forEach((h, i) => {
        h.style.animation = origAnimations[i] || "";
      });

      // Restore inline backgrounds
      targets.forEach((t, i) => {
        t.style.background = origBg[i].bg;
        t.style.backgroundColor = origBg[i].bgColor;
      });

      // Restore [data-disco] elements (dock/drawer)
      document
        .querySelectorAll<HTMLElement>("[data-disco]")
        .forEach((el) => {
          el.style.removeProperty("background");
        });
    }

    return cleanup;
  }, []);

  return null;
}
